import React from "react";
import { initials, scoreColorHard } from "../utils";
import { XIcon } from "./Icons.jsx";

// The one and only collapse chevron used app-wide: a right-pointing ▶ that rotates to point down
// when open, sitting to the LEFT of the title. Keep every roll-up/down control consistent by using
// this everywhere.
export function Chevron({ open, style }) {
  return (
    <span aria-hidden="true" style={{
      fontSize: 11, color: "var(--text-soft)", display: "inline-block", flexShrink: 0, lineHeight: 1,
      transition: "transform .15s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)", ...style,
    }}>▶</span>
  );
}

// Collapsible card shell — click the header to fold. Cards default ROLLED UP (Salem 2026-07-08:
// minimise page space), and each card REMEMBERS the user's last open/closed choice per browser
// (localStorage, keyed by the card title or an explicit `persistKey`). Pass `defaultOpen`
// to override the first-visit state for a card whose content should show immediately.
export function CollapsibleCard({ title, actions, defaultOpen = false, persistKey, style, className = "card", children, titleTag = "h2" }) {
  const key = persistKey || (typeof title === "string" ? title : null);
  const storeKey = key ? `calliq_card_${key.replace(/\s+/g, "_").slice(0, 80)}` : null;
  const [open, setOpen] = React.useState(() => {
    if (storeKey) {
      try {
        const v = localStorage.getItem(storeKey);
        if (v != null) return v === "1";
      } catch { /* private mode etc. */ }
    }
    return defaultOpen;
  });
  const toggle = () => setOpen((v) => {
    const nv = !v;
    if (storeKey) { try { localStorage.setItem(storeKey, nv ? "1" : "0"); } catch { /* ignore */ } }
    return nv;
  });
  const T = titleTag;
  return (
    <div className={className} style={style}>
      <div className="spread" style={{ cursor: "pointer", marginBottom: open ? 12 : 0 }}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        role="button" tabIndex={0} aria-expanded={open}>
        <div className="flex" style={{ gap: 8, alignItems: "center", minWidth: 0 }}>
          <Chevron open={open} />
          <T className="card-title" style={{ margin: 0 }}>{title}</T>
        </div>
        {actions && <div className="flex" style={{ gap: 10, alignItems: "center" }}>{actions}</div>}
      </div>
      {open && children}
    </div>
  );
}

export function Avatar({ name, color, size = 36, photo }) {
  // A photo the browser can't decode (e.g. an iPhone HEIC stored verbatim before the
  // Account-page re-encode, 2026-08-18) must fall back to initials, not an empty circle.
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [photo]);
  if (photo && !broken) {
    return (
      <span className="avatar" title={name}
        style={{ width: size, height: size, padding: 0, overflow: "hidden", background: "var(--card)" }}>
        <img src={photo} alt={name || ""} onError={() => setBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </span>
    );
  }
  return (
    <span
      className="avatar"
      style={{
        width: size,
        height: size,
        background: color || "var(--text-soft)",
        fontSize: Math.max(10, Math.round(size * 0.38)),
      }}
      title={name}
    >
      {initials(name)}
    </span>
  );
}

// A UK-format date field (dd/mm/yyyy) that doesn't depend on the browser locale like the native
// <input type="date"> does. `value`/`onChange` use ISO (yyyy-mm-dd); the user sees dd/mm/yyyy.
// 2026-08-20 (Salem, Add-leave modal): every editable GBDate also carries a 📅 button that opens
// the BROWSER's own calendar — a visually-hidden <input type="date"> driven by showPicker() —
// so a date can be looked up instead of typed. The visible field stays dd/mm/yyyy and fully
// typeable, so the locale problem this component exists to avoid never comes back.
export function GBDate({ value, onChange, style, autoFocus }) {
  const toDisp = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "";
  const [text, setText] = React.useState(toDisp(value));
  const nativeRef = React.useRef(null);
  React.useEffect(() => { setText(toDisp(value)); }, [value]);
  const handle = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length >= 5) out = digits.slice(0, 2) + "/" + digits.slice(2, 4) + "/" + digits.slice(4);
    else if (digits.length >= 3) out = digits.slice(0, 2) + "/" + digits.slice(2);
    setText(out);
    onChange(digits.length === 8 ? `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}` : "");
  };
  // No onChange = display-only usage (e.g. a forecast landing date in a table) — plain field,
  // no picker, exactly as before.
  if (!onChange) {
    return <input className="input" placeholder="dd/mm/yyyy" inputMode="numeric" maxLength={10}
      value={text} readOnly style={style} autoFocus={autoFocus} />;
  }
  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    // showPicker() needs a user gesture and isn't everywhere — fall back to focusing the
    // native input (which shows its own affordance) rather than ever breaking the button.
    try { if (el.showPicker) el.showPicker(); else el.focus(); } catch { el.focus(); }
  };
  return (
    // .input is width:100%, so the wrapper takes the caller's `style` (widths keep working)
    // and the field fills it; the 📅 sits inside the field's right edge.
    <span style={{ position: "relative", display: "block", ...style }}>
      <input className="input" placeholder="dd/mm/yyyy" inputMode="numeric" maxLength={10}
        value={text} onChange={handle} autoFocus={autoFocus} style={{ paddingRight: 32 }} />
      <button type="button" onClick={openPicker} tabIndex={-1}
        aria-label="Pick a date from the calendar" title="Pick a date from the calendar"
        style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)",
          border: "none", background: "transparent", cursor: "pointer", fontSize: 13.5,
          lineHeight: 1, padding: "2px 4px", color: "var(--text-soft)" }}>
        📅
      </button>
      {/* The calendar itself: hidden but rendered (display:none would break showPicker),
          anchored at the field's right edge so the popup opens next to the 📅. */}
      <input ref={nativeRef} type="date" tabIndex={-1} aria-hidden="true"
        value={/^\d{4}-\d{2}-\d{2}$/.test(value || "") ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        style={{ position: "absolute", right: 8, bottom: 0, width: 1, height: 1,
          opacity: 0, border: 0, padding: 0, margin: 0, pointerEvents: "none" }} />
    </span>
  );
}

export function ScoreChip({ score, size = 28, decimals }) {
  if (score == null) return null;
  const txt =
    decimals != null
      ? Number(score).toFixed(decimals)
      : Number.isInteger(Number(score))
        ? String(score)
        : Number(score).toFixed(1);
  return (
    <span
      className="score-chip"
      style={{
        width: size >= 40 ? "auto" : size,
        minWidth: size,
        height: size,
        padding: size >= 40 ? "0 10px" : 0,
        background: scoreColorHard(Number(score)),
        fontSize: Math.max(11, Math.round(size * 0.45)),
      }}
      title={`AI Score: ${txt}`}
    >
      {txt}
    </span>
  );
}

export function Spinner() {
  return <div className="spinner" />;
}

export function Skeleton({ h = 16, w = "100%", style }) {
  return <div className="skeleton" style={{ height: h, width: w, ...style }} />;
}

export function SkeletonRows({ n = 5, h = 48 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} h={h} />
      ))}
    </div>
  );
}

export function EmptyState({ icon = "📭", title = "Nothing here", sub }) {
  return (
    <div className="empty-state">
      <div className="big">{icon}</div>
      <div style={{ fontWeight: 600 }}>{title}</div>
      {sub && <div className="small" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// `full` = near-fullscreen working modal (e.g. Schedule 5 weekly reconciliation) — takes almost
// the whole viewport so long tables get maximum room. Overrides the .modal/.xl size caps inline.
export function Modal({ title, onClose, children, wide, xl, full, footer, zIndex }) {
  const dialogRef = React.useRef(null);
  // Focus the dialog ONCE on open so keyboard users land inside it (and screen readers announce it
  // via role="dialog" + aria-modal). This MUST run only on mount — callers pass an inline arrow as
  // onClose (a new identity every render), so tying the focus() to onClose would re-focus the
  // dialog on every keystroke and steal focus from the field being typed in.
  React.useEffect(() => {
    dialogRef.current?.focus?.();
  }, []);
  // Esc closes. Separate effect so it can track the latest onClose without re-focusing the dialog.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      // Optional stacking override: the Toolkit dock floats at z 300 (above ordinary modal
      // backdrops at 200, so clicking it can't dismiss an open form), and modals opened
      // FROM the dock (the citation viewer) pass a higher z to sit above it (2026-08-18).
      style={zIndex ? { zIndex } : undefined}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={"modal" + (full || xl ? " xl" : wide ? " wide" : "")}
        style={full ? { width: "calc(96vw / var(--ui-zoom, 1))", maxWidth: "calc(96vw / var(--ui-zoom, 1))", height: "calc(94vh / var(--ui-zoom, 1))", maxHeight: "calc(94vh / var(--ui-zoom, 1))",
                        display: "flex", flexDirection: "column", overflow: "auto" } : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="spread" style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <XIcon size={18} />
          </button>
        </div>
        {children}
        {footer && (
          <div className="flex" style={{ justifyContent: "flex-end", marginTop: 18 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
